import React, { useState, useEffect, useMemo } from 'react';
import {
  getAnalyticsSummary,
  getAnalyticsReliability,
  getAnalyticsTruthCheck,
  getAnalyticsLibrary,
  getAnalyticsAirtime,
} from '../api/analyticsApi';
import { getActiveProjectId } from '../constants/projectConstants';

/**
 * AnalyticsView — exec-grade analytics page. Ported from the approved
 * mockup at master-sumadhura-cms/docs/strategy/analytics-final.html.
 *
 * Sections in order:
 *   1. Library (banner + 3 accordion sub-panels)
 *   2. Air time (per-site/per-screen × category mix + incidents lane)
 *   3. Truth check (fault-attributed heatmap)
 *   4. Reliability summary (split fault attribution + per-site table)
 *
 * Data sources — six read-only endpoints under /api/analytics/*. All
 * accept the same from/to/projectId trio.
 *
 * Period switching refetches all 5 sections in parallel (the /summary
 * endpoint is currently unused on the page — kept in the API client for
 * future top stat cards).
 */

const PROJECT_ID = getActiveProjectId();

const PERIODS = {
  week:    { days: 7,  label: 'This week',  asideDays: '7 days' },
  month:   { days: 31, label: 'This month', asideDays: '31 days' },
  quarter: { days: 91, label: 'Quarter',    asideDays: '13 weeks · weekly aggregate' },
};

function computeRange(periodKey) {
  const to = new Date();
  const from = new Date();
  const cfg = PERIODS[periodKey] || PERIODS.month;
  from.setDate(to.getDate() - cfg.days + 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

const STATUS_RANK = { grey: 0, green: 1, blue: 2, orange: 3, red: 4 };
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH   = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Earliest date we actually had CDM telemetry flowing. Any heatmap cell
 * whose date is strictly before this gets forced to "grey · no data"
 * regardless of what /truth-check returns — CDM occasionally emits very
 * long closed-range events (e.g. a 14-month `app_stopped` covering
 * 2025-03 → 2026-05) which the aggregator faithfully clips into the
 * period and paints orange. Pre-cutoff days are days we never actually
 * observed, so painting them as "app issue" misleads the reader.
 *
 * Bump this when a new tenant's CDM goes live. Eventually we should
 * source this from /api/analytics/summary so each tenant carries its
 * own onboarding date.
 */
const CDM_INGESTION_START = '2026-05-12';

const isBeforeCdmStart = (isoDate) => typeof isoDate === 'string' && isoDate < CDM_INGESTION_START;

/**
 * Inclusive count of calendar days in [fromIso, toIso] that are on or
 * after CDM_INGESTION_START. Used to surface "31 days requested, 2
 * actually observable since CDM ingestion went live" so the marketing-
 * head reading the page knows the percentages are computed against a
 * partial window.
 */
function observableDaysInRange(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const cdmMs = new Date(CDM_INGESTION_START + 'T00:00:00Z').getTime();
  const fromMs = Math.max(new Date(fromIso).getTime(), cdmMs);
  const toMs = new Date(toIso).getTime();
  if (fromMs > toMs) return 0;
  return Math.floor((toMs - fromMs) / 86400000) + 1;
}

/**
 * Human duration. Minutes stop being comprehensible past 60, hours
 * past 24. Applied to every dark-time / app-issue / network / device
 * number on the reliability summary so "961 min" becomes "16h 1m" and
 * "1440 min" becomes "1d" rather than blasting the reader with raw
 * minute counts.
 *
 *   m = 0           → "0 min"
 *   m < 60          → "Xm"
 *   m < 24h         → "Xh" or "Xh Ym"
 *   m ≥ 24h         → "Xd" or "Xd Yh"
 */
function fmtDuration(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return '—';
  const m = Math.round(minutes);
  if (m === 0) return '0 min';
  if (m < 60) return `${m}m`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rem = m - h * 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  const d = Math.floor(m / 1440);
  const remH = Math.floor((m - d * 1440) / 60);
  return remH === 0 ? `${d}d` : `${d}d ${remH}h`;
}

function fmtHours(sec) {
  if (sec == null) return '—';
  return Math.round(sec).toLocaleString();
}

// Hours-input variant of fmtDuration. Library + air-time totals come
// back from the API in hours; reuse the m/h/d cascade by feeding
// minutes. "0.05 hr" → "3m", "16.02 hr" → "16h 1m", "27 hr" → "1d 3h".
function fmtDurationHours(hours) {
  if (hours == null || Number.isNaN(hours)) return '—';
  return fmtDuration(hours * 60);
}

const CATEGORY_COLORS = {
  Brand: 'var(--cat-brand)',
  Campaign: 'var(--cat-campaign)',
  Project: 'var(--cat-project)',
  ChannelPartner: 'var(--cat-channel)',
  ExistingCustomers: 'var(--cat-customer)',
  Untagged: 'var(--cat-untagged)',
};

const CATEGORY_LABELS = {
  Brand: 'Brand',
  Campaign: 'Campaign',
  Project: 'Project',
  ChannelPartner: 'Channel Partner',
  ExistingCustomers: 'Existing Customers',
  Untagged: 'Untagged',
};

const STYLE = `
.av-root {
  --gold-500: #c8a147; --gold-700: #9a7a2c;
  --ink-900: #0f1115; --ink-700: #2c313a; --ink-500: #5a626f;
  --ink-300: #b6bcc6; --ink-100: #eceef2; --ink-50: #f6f7f9;
  --ok-500: #2da06b; --warn-500: #d6890b; --bad-500: #c14747;
  --fault-green: #2da06b; --fault-blue: #2563eb;
  --fault-orange: #d6890b; --fault-red: #c14747; --fault-grey: #cdd2d8;
  --cat-brand: #4f46e5; --cat-campaign: #c8a147; --cat-project: #2da06b;
  --cat-channel: #8b5cf6; --cat-customer: #0ea5b7; --cat-untagged: #9ca3af;

  font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
  color: var(--ink-900);
  background: var(--ink-50);
  min-height: 100%;
  padding: 24px;
}
.av-page { max-width: 1180px; margin: 0 auto; }
.av-head { display: flex; justify-content: flex-end; margin-bottom: 20px; }
.av-period-picker { display: flex; gap: 6px; }
.av-pill {
  padding: 6px 12px; font-size: 12px; font-weight: 600;
  border-radius: 6px; background: white; border: 1px solid var(--ink-100);
  color: var(--ink-500); cursor: pointer; font-family: inherit;
}
.av-pill:hover { color: var(--ink-900); border-color: var(--ink-300); }
.av-pill.active { background: var(--ink-900); color: white; border-color: var(--ink-900); }
.av-section { background: white; border: 1px solid var(--ink-100); border-radius: 12px; padding: 20px 24px; margin-bottom: 20px; }
.av-section-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; }
.av-section-title { font-size: 12px; color: var(--ink-500); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
.av-section-q { font-size: 16px; color: var(--ink-900); font-weight: 600; margin: 2px 0 0; }
.av-section-aside { font-size: 12px; color: var(--ink-500); }
.av-loading { padding: 60px 20px; text-align: center; color: var(--ink-500); font-size: 13px; }
.av-error { padding: 16px; background: rgba(193, 71, 71, 0.08); border: 1px solid rgba(193, 71, 71, 0.2); border-radius: 8px; color: var(--bad-500); font-size: 13px; }

/* Library */
.av-lib-stats { display: flex; align-items: stretch; gap: 4px; padding: 6px; margin-bottom: 16px; background: var(--ink-50); border: 1px solid var(--ink-100); border-radius: 8px; }
.av-lb-fact, .av-lb-stat { display: inline-flex; align-items: baseline; gap: 8px; padding: 10px 14px; border-radius: 6px; color: inherit; text-decoration: none; }
.av-lb-fact { background: white; box-shadow: 0 0 0 1px var(--ink-100); cursor: default; }
.av-lb-stat { cursor: pointer; border: none; background: transparent; font-family: inherit; }
.av-lb-stat:hover { background: white; box-shadow: 0 0 0 1px var(--ink-100); }
.av-lb-num { font-size: 18px; font-weight: 700; color: var(--ink-900); font-variant-numeric: tabular-nums; line-height: 1; }
.av-lb-num.warn { color: var(--warn-500); }
.av-lb-num.ok   { color: var(--ok-500); }
.av-lb-label { font-size: 12px; color: var(--ink-500); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
.av-lb-divider { width: 1px; background: var(--ink-300); margin: 10px 8px; }

.av-lib-panel { border: 1px solid var(--ink-100); border-radius: 10px; margin-bottom: 10px; background: white; }
.av-lib-panel-head { display: flex; align-items: center; gap: 10px; padding: 12px 16px; cursor: pointer; background: none; border: none; width: 100%; text-align: left; font-family: inherit; }
.av-lib-panel-head:hover:not([disabled]) { background: var(--ink-50); }
.av-lib-panel-head[disabled] { cursor: default; opacity: 0.65; }
.av-lib-panel.expanded .av-lib-panel-head { background: var(--ink-50); border-bottom: 1px solid var(--ink-100); }
.av-lib-chevron { width: 10px; height: 10px; transition: transform .15s; color: var(--ink-300); flex-shrink: 0; }
.av-lib-panel.expanded .av-lib-chevron { transform: rotate(90deg); color: var(--ink-500); }
.av-lib-title { font-size: 14px; font-weight: 600; color: var(--ink-900); flex: 1; }
.av-lib-meta { font-size: 12px; color: var(--ink-500); }
.av-lp-count { font-weight: 700; color: var(--ink-700); margin-right: 4px; }
.av-lp-count.warn { color: var(--warn-500); }
.av-lp-count.ok   { color: var(--ok-500); }
.av-lib-body { display: none; padding: 8px 16px 14px; }
.av-lib-panel.expanded .av-lib-body { display: block; }

.av-creative-row { display: grid; grid-template-columns: 80px 1fr 110px; gap: 14px; align-items: center; padding: 10px 6px; text-decoration: none; color: inherit; }
.av-creative-row + .av-creative-row { border-top: 1px solid var(--ink-100); }
.av-creative-row:hover { background: var(--ink-50); }
.av-creative-thumb-wrap { position: relative; }
.av-creative-thumb { width: 80px; height: 52px; border-radius: 5px; background: var(--ink-300); display: flex; align-items: center; justify-content: center; color: white; font-size: 11px; font-weight: 700; overflow: hidden; }
.av-creative-thumb.has-media { cursor: zoom-in; padding: 0; background: var(--ink-900); }
.av-creative-thumb img, .av-creative-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; }
.av-thumb-preview { position: absolute; left: 0; top: calc(100% + 6px); width: 260px; height: 165px; border-radius: 6px; box-shadow: 0 10px 28px rgba(0,0,0,0.22); background: var(--ink-900); z-index: 30; overflow: hidden; pointer-events: none; }
.av-thumb-preview img, .av-thumb-preview video { width: 100%; height: 100%; object-fit: contain; display: block; }
.av-creative-id { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.av-creative-name { font-size: 13px; font-weight: 500; color: var(--ink-900); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.av-cat-chip { align-self: flex-start; font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 3px; line-height: 1.4; }
.av-creative-hours { font-size: 13px; font-weight: 600; color: var(--ink-900); text-align: right; font-variant-numeric: tabular-nums; }
.av-creative-hours .unit { font-weight: 400; color: var(--ink-500); font-size: 11px; }

.av-cat-Brand { background: rgba(79,70,229,0.12); color: var(--cat-brand); }
.av-cat-Campaign { background: rgba(200,161,71,0.16); color: var(--cat-campaign); }
.av-cat-Project { background: rgba(45,160,107,0.14); color: var(--cat-project); }
.av-cat-ChannelPartner { background: rgba(139,92,246,0.12); color: var(--cat-channel); }
.av-cat-ExistingCustomers { background: rgba(14,165,183,0.12); color: var(--cat-customer); }
.av-cat-Untagged { background: rgba(156,163,175,0.18); color: var(--ink-500); }

/* Airtime */
.av-airtime { display: flex; flex-direction: column; gap: 2px; }
.av-at-row { display: grid; grid-template-columns: 260px 1fr 110px; gap: 14px; align-items: center; padding: 8px 6px; border-radius: 6px; }
.av-at-row.site .toggle { display: grid; grid-template-columns: 14px 1fr; column-gap: 8px; row-gap: 1px; cursor: pointer; background: none; border: none; padding: 0; font-size: 13px; font-weight: 500; color: var(--ink-700); text-align: left; width: 100%; font-family: inherit; min-width: 0; }
.av-at-row.site .toggle .av-chevron { grid-row: 1 / span 2; align-self: center; }
.av-at-row.site .toggle .site-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.av-chevron { width: 10px; height: 10px; transition: transform .15s; color: var(--ink-300); flex-shrink: 0; }
.av-at-row.site.expanded .av-chevron { transform: rotate(90deg); color: var(--ink-500); }
.av-at-row.screen { display: none; padding: 4px 6px 4px 28px; }
.av-at-row.screen.shown { display: grid; }
.av-at-row.screen .screen-label { font-size: 12px; color: var(--ink-500); }
.av-at-row.screen .av-bar { height: 10px; }
.av-bar { display: flex; height: 18px; border-radius: 4px; overflow: hidden; background: var(--ink-100); }
.av-bar .seg { height: 100%; cursor: pointer; }
.av-at-hours { font-size: 13px; font-weight: 600; color: var(--ink-900); text-align: right; font-variant-numeric: tabular-nums; }
.av-at-hours .unit { font-weight: 400; color: var(--ink-500); font-size: 11px; }
.av-at-subhours { font-size: 11px; font-weight: 400; color: var(--ink-500); margin-top: 2px; font-variant-numeric: tabular-nums; }
.av-at-screencount { font-size: 11px; font-weight: 400; color: var(--ink-500); white-space: nowrap; }
.av-at-legend { display: flex; flex-wrap: wrap; gap: 14px; padding: 8px 0 12px; }
.av-at-legend__item { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: var(--ink-700); }
.av-at-legend__item.is-absent { opacity: 0.4; }
.av-at-legend__sw { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }

/* Truth check heatmap */
.av-hm-wrap { overflow-x: auto; }
.av-hm { display: grid; gap: 4px 3px; align-items: center; }
.av-hm-day-axis { display: contents; }
.av-hm-blank, .av-hm-day-label { font-size: 9px; color: var(--ink-500); text-align: center; padding: 2px 0; }
.av-hm-day-label.month-boundary { color: var(--ink-900); font-weight: 700; }
.av-hm-day-label.first-cell { color: var(--ink-900); font-weight: 700; }
.av-hm-row { display: contents; }
.av-hm-row .site-label { font-size: 12px; font-weight: 500; color: var(--ink-700); padding: 4px 8px 4px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.av-hm-row.site .site-label { display: flex; align-items: center; gap: 6px; background: none; border: none; cursor: pointer; font: inherit; text-align: left; font-family: inherit; }
.av-hm-row.site .site-label > span { overflow: hidden; text-overflow: ellipsis; }
.av-hm-row.screen { display: none; }
.av-hm-row.screen.shown { display: contents; }
.av-hm-row.screen .site-label { font-size: 11px; color: var(--ink-500); font-weight: 400; padding-left: 22px; }
.av-hm-row.screen .av-cell { transform: scale(0.8); }
.av-cell { aspect-ratio: 1 / 1; border-radius: 3px; cursor: pointer; }
.av-cell:hover { outline: 2px solid var(--ink-300); outline-offset: 1px; }
.av-cell.green  { background: var(--fault-green); }
.av-cell.blue   { background: var(--fault-blue); }
.av-cell.orange { background: var(--fault-orange); }
.av-cell.red    { background: var(--fault-red); }
.av-cell.grey   { background: var(--fault-grey); }
.av-hm-legend { display: flex; gap: 18px; margin-top: 14px; font-size: 11px; color: var(--ink-500); flex-wrap: wrap; }
.av-hm-legend .ll { display: inline-flex; align-items: center; gap: 6px; }
.av-hm-legend .ll .sw { width: 12px; height: 12px; border-radius: 3px; }
.av-hm-legend .ll strong { color: var(--ink-700); }

/* Reliability */
.av-rel-headline { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 16px 18px; background: var(--ink-50); border-radius: 10px; margin-bottom: 16px; }
.av-rh-num { display: flex; flex-direction: column; gap: 4px; }
.av-rh-num .big { font-size: 22px; font-weight: 700; color: var(--ink-900); line-height: 1; font-variant-numeric: tabular-nums; }
.av-rh-num .big.our    { color: var(--fault-orange); }
.av-rh-num .big.net    { color: var(--fault-blue); }
.av-rh-num .big.device { color: var(--fault-red); }
.av-rh-num .big.zero   { color: var(--ok-500); }
.av-rh-num .big-l { font-size: 11px; color: var(--ink-500); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
.av-rh-num .big-sub { font-size: 11px; color: var(--ink-500); margin-top: 2px; }
.av-rh-num .big-sub.ok { color: var(--ok-500); font-weight: 600; }
.av-rb-eyebrow { font-size: 11px; color: var(--ink-500); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; margin-bottom: 8px; display: flex; justify-content: space-between; }
.av-rb-eyebrow strong { color: var(--ink-900); }
.av-rb-bar { display: flex; height: 14px; border-radius: 7px; overflow: hidden; background: var(--fault-green); }
.av-rb-seg { color: white; font-size: 10px; font-weight: 600; display: flex; align-items: center; padding: 0 8px; }
.av-rb-seg.our { background: var(--fault-orange); }
.av-rb-seg.net { background: var(--fault-blue); }
.av-rb-seg.device { background: var(--fault-red); }
.av-rb-legend { display: flex; gap: 18px; margin-top: 12px; font-size: 12px; color: var(--ink-700); flex-wrap: wrap; }
.av-rb-legend strong { color: var(--ink-900); }
.av-rb-sw { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 6px; vertical-align: -1px; }
.av-rb-sw.our { background: var(--fault-orange); }
.av-rb-sw.net { background: var(--fault-blue); }
.av-rb-sw.device { background: var(--fault-red); }
.av-rel-sites { margin-top: 18px; border-top: 1px solid var(--ink-100); padding-top: 16px; }
.av-rs-eyebrow { font-size: 11px; color: var(--ink-500); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; margin-bottom: 10px; }
.av-rs-row { display: grid; grid-template-columns: 220px 1fr 70px; gap: 14px; padding: 10px 12px; border-radius: 6px; align-items: center; }
.av-rs-row:hover { background: var(--ink-50); }
.av-rs-row + .av-rs-row { border-top: 1px solid var(--ink-100); }
.av-rs-name { font-size: 13px; font-weight: 500; color: var(--ink-900); }
.av-rs-name .meta { font-size: 11px; color: var(--ink-500); margin-left: 6px; }
.av-rs-pct { font-size: 13px; font-weight: 700; text-align: right; font-variant-numeric: tabular-nums; }
.av-rs-bar { display: flex; height: 10px; border-radius: 5px; overflow: hidden; background: var(--fault-green); }
.av-rs-bar .seg { height: 100%; }
`;

// ─── Period picker ───
function PeriodPicker({ value, onChange }) {
  return (
    <div className="av-head">
      <div className="av-period-picker">
        {['week', 'month', 'quarter'].map((k) => (
          <button
            key={k}
            className={`av-pill ${value === k ? 'active' : ''}`}
            onClick={() => onChange(k)}
          >
            {PERIODS[k].label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Library section ───
function LibrarySection({ data, loading, error, period }) {
  const [expanded, setExpanded] = useState(null);
  if (loading) return <div className="av-section"><div className="av-loading">Loading library…</div></div>;
  if (error)   return <div className="av-section"><div className="av-error">Failed to load library: {error}</div></div>;
  if (!data)   return null;

  const { totals = {}, topPerformers = [], dormant = [], neverPlayed = [] } = data;
  const toggle = (key) => setExpanded(expanded === key ? null : key);

  return (
    <div className="av-section">
      <div className="av-section-head">
        <div>
          <div className="av-section-title">Library</div>
          <div className="av-section-q">What's airing, and what isn't</div>
        </div>
        <div className="av-section-aside">{PERIODS[period]?.asideDays}</div>
      </div>

      <div className="av-lib-stats">
        <div className="av-lb-fact"><span className="av-lb-num">{totals.inLibrary ?? 0}</span><span className="av-lb-label">in library</span></div>
        <span className="av-lb-divider" />
        <button className="av-lb-stat" onClick={() => toggle('played')}>
          <span className="av-lb-num">{totals.playedInPeriod ?? 0}</span><span className="av-lb-label">played this period</span>
        </button>
        <button className="av-lb-stat" onClick={() => toggle('dormant')}>
          <span className={`av-lb-num ${(totals.dormant ?? 0) > 0 ? 'warn' : ''}`}>{totals.dormant ?? 0}</span><span className="av-lb-label">dormant</span>
        </button>
        <button className="av-lb-stat" onClick={() => totals.neverPlayed > 0 && toggle('never')}>
          <span className={`av-lb-num ${(totals.neverPlayed ?? 0) === 0 ? 'ok' : 'warn'}`}>{totals.neverPlayed ?? 0}</span><span className="av-lb-label">never played</span>
        </button>
      </div>

      <LibPanel
        open={expanded === 'played'}
        onToggle={() => toggle('played')}
        title="Most played this period"
        meta={<>Top <span className="av-lp-count">{topPerformers.length}</span> of {totals.playedInPeriod ?? 0} played · ranked by hours after uptime</>}
      >
        {topPerformers.length === 0 ? <div className="av-loading">No plays in this period.</div> : topPerformers.map((c) => (
          <CreativeRow key={c.contentId} c={c} />
        ))}
      </LibPanel>

      <LibPanel
        open={expanded === 'dormant'}
        onToggle={() => toggle('dormant')}
        title="Dormant this period"
        meta={<><span className={`av-lp-count ${dormant.length > 0 ? 'warn' : ''}`}>{dormant.length}</span>in library · scheduled before, didn't air in this period</>}
      >
        {dormant.length === 0 ? <div className="av-loading">Nothing dormant — everything in the library aired this period.</div> : dormant.map((c) => (
          <DormantRow key={c.contentId} c={c} />
        ))}
      </LibPanel>

      <LibPanel
        open={false}
        disabled={totals.neverPlayed === 0}
        title="Never played anywhere"
        meta={(totals.neverPlayed ?? 0) === 0
          ? <span style={{ color: 'var(--ok-500)', fontWeight: 600 }}>✓ 0 — every creative in the library has aired at least once</span>
          : <><span className="av-lp-count warn">{totals.neverPlayed}</span>uploaded but never put into rotation</>}
      />
    </div>
  );
}

function LibPanel({ open, disabled, onToggle, title, meta, children }) {
  return (
    <div className={`av-lib-panel ${open ? 'expanded' : ''}`}>
      <button className="av-lib-panel-head" disabled={disabled} onClick={disabled ? undefined : onToggle}>
        <svg className="av-lib-chevron" viewBox="0 0 10 10" fill="currentColor"><path d="M3 1l4 4-4 4z"/></svg>
        <span className="av-lib-title">{title}</span>
        <span className="av-lib-meta">{meta}</span>
      </button>
      {open && <div className="av-lib-body">{children}</div>}
    </div>
  );
}

function CreativeThumb({ url, mediaType, type, fallbackBg, size }) {
  const [hover, setHover] = useState(false);
  const hasMedia = !!url;
  const isVideo = mediaType === 'video';
  const thumbStyle = hasMedia
    ? { width: size?.w ?? 80, height: size?.h ?? 52 }
    : { width: size?.w ?? 80, height: size?.h ?? 52, background: fallbackBg };
  return (
    <div
      className="av-creative-thumb-wrap"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className={`av-creative-thumb${hasMedia ? ' has-media' : ''}`} style={thumbStyle}>
        {hasMedia
          ? (isVideo
              ? <video src={url} muted playsInline preload="metadata" />
              : <img src={url} alt="" loading="lazy" />)
          : ((type || '').toUpperCase().slice(0, 3) || 'MED')}
      </div>
      {hover && hasMedia && (
        <div className="av-thumb-preview">
          {isVideo
            ? <video src={url} muted autoPlay loop playsInline />
            : <img src={url} alt="" />}
        </div>
      )}
    </div>
  );
}

function CreativeRow({ c }) {
  const cat = c.category || 'Untagged';
  const catLabel = CATEGORY_LABELS[cat] || cat;
  return (
    <div className="av-creative-row">
      <CreativeThumb
        url={c.thumbnailUrl}
        mediaType={c.mediaType || c.type}
        type={c.type}
        fallbackBg="linear-gradient(135deg, #14532d, #2da06b 60%, #6ec096)"
      />
      <div className="av-creative-id">
        <span className="av-creative-name">{c.name || c.contentId}</span>
        <span className={`av-cat-chip av-cat-${cat}`}>{catLabel}</span>
      </div>
      <div className="av-creative-hours">{fmtDurationHours(c.hours)}</div>
    </div>
  );
}

function DormantRow({ c }) {
  const cat = c.category || 'Untagged';
  const catLabel = CATEGORY_LABELS[cat] || cat;
  return (
    <div className="av-creative-row" style={{ gridTemplateColumns: '60px 1fr' }}>
      <CreativeThumb
        url={c.thumbnailUrl}
        mediaType={c.mediaType || c.type}
        type={c.type}
        fallbackBg="linear-gradient(135deg, #1e1b4b, #4f46e5 70%, #a5b4fc)"
        size={{ w: 60, h: 38 }}
      />
      <div className="av-creative-id">
        <span className="av-creative-name">{c.name || c.contentId}</span>
        <span className={`av-cat-chip av-cat-${cat}`}>{catLabel}</span>
      </div>
    </div>
  );
}

// ─── Air-time section ───
function AirtimeSection({ data, loading, error, period }) {
  const [expandedSites, setExpandedSites] = useState({});
  if (loading) return <div className="av-section"><div className="av-loading">Loading air time…</div></div>;
  if (error)   return <div className="av-section"><div className="av-error">Failed to load air time: {error}</div></div>;
  if (!data?.rows) return null;

  const toggle = (slug) => setExpandedSites((s) => ({ ...s, [slug]: !s[slug] }));

  return (
    <div className="av-section">
      <div className="av-section-head">
        <div>
          <div className="av-section-title">Air time</div>
          <div className="av-section-q">What kind of content played, where?</div>
        </div>
        <div className="av-section-aside">Hours by category · {PERIODS[period]?.asideDays}</div>
      </div>

      <AirtimeLegend rows={data.rows} />

      <div className="av-airtime">
        {data.rows.map((row) => (
          <SiteAirtime key={row.groupKey} row={row} expanded={!!expandedSites[row.groupKey]} onToggle={() => toggle(row.groupKey)} />
        ))}
      </div>
    </div>
  );
}

function SiteAirtime({ row, expanded, onToggle }) {
  const totalHours = row.totalPlayedHours || 0;
  const cats = row.byCategory || {};
  const totalCatHours = Object.values(cats).reduce((a, b) => a + b, 0) || 1;
  const screenCount = (row.screens || []).length;
  // Site totals sum every screen's played hours — show avg per screen so
  // "37d 10h across 8 screens" reads as a 4.7d per-screen average, not as
  // a single-device runtime that exceeds the 31-day window.
  const avgHoursPerScreen = screenCount > 0 ? totalHours / screenCount : 0;
  return (
    <>
      <div className={`av-at-row site ${expanded ? 'expanded' : ''}`}>
        <button className="toggle" onClick={onToggle} title={row.groupName}>
          <svg className="av-chevron" viewBox="0 0 10 10" fill="currentColor"><path d="M3 1l4 4-4 4z"/></svg>
          <span className="site-name">{row.groupName}</span>
          {screenCount > 0 && (
            <span className="av-at-screencount">{screenCount} screen{screenCount === 1 ? '' : 's'}</span>
          )}
        </button>
        <div className="av-bar">
          {Object.entries(cats).map(([cat, hrs]) => (
            <div
              key={cat}
              className="seg"
              style={{ width: `${(hrs / totalCatHours * 100).toFixed(1)}%`, background: CATEGORY_COLORS[cat] || CATEGORY_COLORS.Untagged }}
              title={`${CATEGORY_LABELS[cat] || cat} · ${fmtDurationHours(hrs)}`}
            />
          ))}
        </div>
        <div className="av-at-hours">
          {fmtDurationHours(totalHours)}
          {screenCount > 1 && (
            <div className="av-at-subhours">{fmtDurationHours(avgHoursPerScreen)} / screen</div>
          )}
        </div>
      </div>
      {(row.screens || []).map((s) => (
        <div key={s.groupKey} className={`av-at-row screen ${expanded ? 'shown' : ''}`}>
          <div className="screen-label">↳ {s.groupName}</div>
          <div className="av-bar">
            {Object.entries(s.byCategory || {}).map(([cat, hrs]) => {
              const screenTotal = Object.values(s.byCategory || {}).reduce((a, b) => a + b, 0) || 1;
              return (
                <div
                  key={cat}
                  className="seg"
                  style={{ width: `${(hrs / screenTotal * 100).toFixed(1)}%`, background: CATEGORY_COLORS[cat] || CATEGORY_COLORS.Untagged }}
                  title={`${CATEGORY_LABELS[cat] || cat} · ${fmtDurationHours(hrs)}`}
                />
              );
            })}
          </div>
          <div className="av-at-hours">{fmtDurationHours(s.totalPlayedHours || 0)}</div>
        </div>
      ))}
    </>
  );
}

// Legend listing every category in the taxonomy, dimming the ones that
// didn't appear in this period's data. Showing the full set teaches the
// taxonomy at a glance — viewers learn what tags exist even when no
// content is tagged with them this month.
function AirtimeLegend({ rows }) {
  const present = new Set();
  for (const r of rows || []) {
    for (const k of Object.keys(r.byCategory || {})) present.add(k);
    for (const s of r.screens || []) {
      for (const k of Object.keys(s.byCategory || {})) present.add(k);
    }
  }
  const order = ["Brand", "Campaign", "Project", "ChannelPartner", "ExistingCustomers", "Untagged"];
  return (
    <div className="av-at-legend">
      {order.map((cat) => {
        const isPresent = present.has(cat);
        return (
          <div
            key={cat}
            className={`av-at-legend__item${isPresent ? '' : ' is-absent'}`}
            title={isPresent ? undefined : 'No content tagged this period'}
          >
            <span className="av-at-legend__sw" style={{ background: CATEGORY_COLORS[cat] }} />
            <span>{CATEGORY_LABELS[cat] || cat}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Truth-check section ───
function TruthCheckSection({ data, period, loading, error }) {
  // ALL hooks at the top — React requires a stable hook order across
  // renders. Calling useMemo after a conditional early-return causes
  // "Rendered more hooks than during the previous render" (React #310).
  const [expandedSites, setExpandedSites] = useState({});
  const cellMode = period === 'quarter' ? 'week' : 'day';
  const cells = useMemo(
    () => enumerateCells(data?.period, cellMode),
    [data?.period?.from, data?.period?.to, cellMode]
  );

  if (loading) return <div className="av-section"><div className="av-loading">Loading truth check…</div></div>;
  if (error)   return <div className="av-section"><div className="av-error">Failed to load truth check: {error}</div></div>;
  if (!data?.sites) return null;

  const cellMin = cells.length <= 7 ? '38px' : cells.length <= 14 ? '30px' : '18px';
  const cellMax = cells.length <= 7 ? '56px' : cells.length <= 14 ? '52px' : '40px';
  const gridCols = `220px repeat(${cells.length}, minmax(${cellMin}, ${cellMax}))`;

  const toggle = (slug) => setExpandedSites((s) => ({ ...s, [slug]: !s[slug] }));

  return (
    <div className="av-section">
      <div className="av-section-head">
        <div>
          <div className="av-section-title">Truth check</div>
          <div className="av-section-q">Day-by-day · when something happened, whose fault?</div>
        </div>
        <div className="av-section-aside">{PERIODS[period]?.asideDays} · click a site to expand its screens</div>
      </div>

      <div className="av-hm-wrap">
        <div className="av-hm" style={{ gridTemplateColumns: gridCols }}>
          <div className="av-hm-day-axis">
            <div className="av-hm-blank" />
            {cells.map((c, i) => {
              const cls = ['av-hm-day-label'];
              if (i === 0) cls.push('first-cell');
              else if (c.isMonthBoundary) cls.push('month-boundary');
              return <div key={i} className={cls.join(' ')}>{c.label}</div>;
            })}
          </div>
          {data.sites.map((site) => (
            <SiteHeatmapRow key={site.siteSlug} site={site} cells={cells} expanded={!!expandedSites[site.siteSlug]} onToggle={() => toggle(site.siteSlug)} />
          ))}
        </div>
      </div>

      <div className="av-hm-legend">
        <span className="ll"><span className="sw" style={{ background: 'var(--fault-green)' }} /><strong>All up</strong> · nothing went wrong</span>
        <span className="ll"><span className="sw" style={{ background: 'var(--fault-blue)' }} /><strong>Site network</strong> · internet drop, app stayed up</span>
        <span className="ll"><span className="sw" style={{ background: 'var(--fault-orange)' }} /><strong>App issue</strong> · our app crashed/stopped</span>
        <span className="ll"><span className="sw" style={{ background: 'var(--fault-red)' }} /><strong>Device off</strong> · power loss or reboot</span>
        <span className="ll"><span className="sw" style={{ background: 'var(--fault-grey)' }} /><strong>No data</strong> · device not yet onboarded</span>
      </div>
    </div>
  );
}

// A cell is "pre-CDM" if every date it covers is before the ingestion
// cutoff. For week-aggregate cells (quarter view), a single in-cutoff
// date is enough to count it as "have data" — partial weeks aren't
// silenced just because some of their days predate ingestion.
function isCellBeforeCdmStart(cell) {
  if (!cell || !cell.dates || cell.dates.length === 0) return false;
  return cell.dates.every(isBeforeCdmStart);
}

function SiteHeatmapRow({ site, cells, expanded, onToggle }) {
  const aggregateForCell = (cell) => {
    if (isCellBeforeCdmStart(cell)) return 'grey';
    let worst = 'green';
    for (const dev of (site.devices || [])) {
      for (const day of (dev.days || [])) {
        if (!cell.dates.includes(day.date)) continue;
        if (isBeforeCdmStart(day.date)) continue;
        if (STATUS_RANK[day.status] > STATUS_RANK[worst]) worst = day.status;
      }
    }
    return worst;
  };
  return (
    <>
      <div className="av-hm-row site">
        <button className="site-label" onClick={onToggle}>
          <svg className="av-chevron" viewBox="0 0 10 10" fill="currentColor" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}><path d="M3 1l4 4-4 4z"/></svg>
          <span>{site.siteName}</span>
        </button>
        {cells.map((c, i) => {
          const s = aggregateForCell(c);
          const baseTip = `${site.siteName} · ${c.label}`;
          const tip = s === 'grey' ? `${baseTip} · no CDM data yet (ingestion started ${CDM_INGESTION_START})` : baseTip;
          return <div key={i} className={`av-cell ${s}`} title={tip} />;
        })}
      </div>
      {(site.devices || []).map((dev) => (
        <div key={dev.deviceId} className={`av-hm-row screen ${expanded ? 'shown' : ''}`}>
          <div className="site-label">↳ {dev.displayName}</div>
          {cells.map((c, i) => {
            if (isCellBeforeCdmStart(c)) {
              return <div key={i} className="av-cell grey" title={`${dev.displayName} · ${c.label} · no CDM data yet`} />;
            }
            let worst = 'green';
            const incidents = [];
            for (const day of (dev.days || [])) {
              if (!c.dates.includes(day.date)) continue;
              if (isBeforeCdmStart(day.date)) continue;
              if (STATUS_RANK[day.status] > STATUS_RANK[worst]) worst = day.status;
              (day.incidents || []).forEach((x) => incidents.push(`${day.date}: ${x.type}`));
            }
            return <div key={i} className={`av-cell ${worst}`} title={incidents.join(' · ') || `${dev.displayName} · ${c.label}`} />;
          })}
        </div>
      ))}
    </>
  );
}

function enumerateCells(periodMeta, cellMode) {
  if (!periodMeta) return [];
  const from = new Date(periodMeta.from);
  const to   = new Date(periodMeta.to);
  const cells = [];
  if (cellMode === 'day') {
    const dayCount = Math.round((to - from) / 86400000);
    let c = new Date(from);
    let firstCell = true;
    while (c <= to) {
      const dayNum = c.getUTCDate();
      // Surface the month explicitly on the first cell of the period AND
      // every change-of-month boundary (day=1). Bare day numbers alone are
      // confusing — readers can't tell whether "13" is April or May without
      // tracking the wrap by themselves.
      const showMonth = firstCell || dayNum === 1;
      const monthAbbr = MONTH[c.getUTCMonth()];
      let label;
      if (dayCount <= 14) {
        label = showMonth ? `${monthAbbr} ${dayNum}` : `${WEEKDAY[c.getUTCDay()]} ${dayNum}`;
      } else {
        label = showMonth ? `${monthAbbr} ${dayNum}` : String(dayNum);
      }
      cells.push({
        dates: [c.toISOString().slice(0, 10)],
        label,
        isMonthBoundary: showMonth && !firstCell,
      });
      firstCell = false;
      c.setUTCDate(c.getUTCDate() + 1);
    }
  } else {
    let c = new Date(from);
    while (c <= to) {
      const weekDates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(c); d.setUTCDate(d.getUTCDate() + i);
        if (d <= to) weekDates.push(d.toISOString().slice(0, 10));
      }
      cells.push({
        dates: weekDates,
        label: `${MONTH[c.getUTCMonth()]} ${c.getUTCDate()}`,
        isMonthBoundary: false,
      });
      c.setUTCDate(c.getUTCDate() + 7);
    }
  }
  return cells;
}

// ─── Reliability section ───
function ReliabilitySection({ data, loading, error, period }) {
  if (loading) return <div className="av-section"><div className="av-loading">Loading reliability…</div></div>;
  if (error)   return <div className="av-section"><div className="av-error">Failed to load reliability: {error}</div></div>;
  if (!data)   return null;

  const { totals = {}, breakdown = {}, bySite = [], period: respPeriod } = data;
  const ourMin = breakdown.app?.minutes ?? 0;
  const netMin = breakdown.network?.minutes ?? 0;
  const devMin = breakdown.device?.minutes ?? 0;
  const darkMin = totals.darkMinutes ?? 0;
  const scheduledMin = totals.scheduledMinutes ?? 0;
  const uptimeMin = Math.max(0, scheduledMin - darkMin);
  const uptimePct = totals.uptimePct ?? 100;

  // Where is the app-issue time concentrated? Surface the top contributor
  // so the headline isn't read as "the platform's app is broken" when it's
  // really one device with bad telemetry. Uses backend bySite breakdown.
  const appBySite = (bySite || [])
    .filter((s) => (s.breakdown?.app?.minutes ?? 0) > 0)
    .sort((a, b) => (b.breakdown?.app?.minutes ?? 0) - (a.breakdown?.app?.minutes ?? 0));
  let appAttribution = null;
  if (appBySite.length === 1) {
    appAttribution = `all on ${appBySite[0].siteName}`;
  } else if (appBySite.length > 1) {
    const top = appBySite[0];
    const share = (top.breakdown?.app?.minutes ?? 0) / ourMin;
    if (share >= 0.8) appAttribution = `mostly on ${top.siteName}`;
    else appAttribution = `${appBySite.length} sites · top ${top.siteName}`;
  }

  // Observable-window disclaimer. Tells the marketing head that the % is
  // computed against only the days CDM was actually emitting telemetry,
  // not the full requested window.
  const requestedDays = PERIODS[period]?.days ?? 31;
  const observable = respPeriod
    ? observableDaysInRange(respPeriod.from?.slice(0, 10), respPeriod.to?.slice(0, 10))
    : 0;
  const observableLabel = observable < requestedDays
    ? `${requestedDays} days · ${observable} observable since CDM ingestion`
    : `${requestedDays} days`;

  return (
    <div className="av-section">
      <div className="av-section-head">
        <div>
          <div className="av-section-title">Reliability summary</div>
          <div className="av-section-q">Fleet uptime — and when something went dark, whose fault?</div>
        </div>
        <div className="av-section-aside">{observableLabel}</div>
      </div>

      <div className="av-rel-headline">
        <div className="av-rh-num">
          <span className="big">{uptimePct.toFixed(1)}%</span>
          <span className="big-l">Fleet uptime</span>
          <span className="big-sub">{fmtDuration(uptimeMin)} up of {fmtDuration(scheduledMin)} scheduled</span>
        </div>
        <div className="av-rh-num">
          <span className={`big ${ourMin === 0 ? 'zero' : 'our'}`}>{fmtDuration(ourMin)}</span>
          <span className="big-l">App issue</span>
          <span className={`big-sub ${ourMin === 0 ? 'ok' : ''}`}>
            {ourMin === 0
              ? '✓ Zero app failures this period'
              : (appAttribution || `${breakdown.app?.pct ?? 0}% of scheduled`)}
          </span>
        </div>
        <div className="av-rh-num">
          <span className={`big ${netMin === 0 ? 'zero' : 'net'}`}>{fmtDuration(netMin)}</span>
          <span className="big-l">Site network</span>
          <span className="big-sub">Their WiFi/internet</span>
        </div>
        <div className="av-rh-num">
          <span className={`big ${devMin === 0 ? 'zero' : 'device'}`}>{fmtDuration(devMin)}</span>
          <span className="big-l">Device off / reboot</span>
          <span className="big-sub">Power loss or planned restart</span>
        </div>
      </div>

      {/* Bar shows the WHOLE scheduled window, not just dark time. Green
          dominates at ~97%; the colored slivers at the right edge are the
          three fault buckets. Reading it: "almost all green" is the first
          impression — opposite of the old bar that was 100%-colored. */}
      <div>
        <div className="av-rb-eyebrow">
          <span>Fleet time</span>
          <span>{fmtDuration(uptimeMin)} up · {fmtDuration(darkMin)} dark</span>
        </div>
        <div className="av-rb-bar">
          <div className="av-rb-seg up" style={{ width: `${(uptimeMin / scheduledMin * 100).toFixed(3)}%`, background: 'var(--fault-green)' }} title={`Up · ${fmtDuration(uptimeMin)}`} />
          {ourMin > 0 && <div className="av-rb-seg our"    style={{ width: `${(ourMin / scheduledMin * 100).toFixed(3)}%`, minWidth: 6 }} title={`App issue · ${fmtDuration(ourMin)}`} />}
          {netMin > 0 && <div className="av-rb-seg net"    style={{ width: `${(netMin / scheduledMin * 100).toFixed(3)}%`, minWidth: 6 }} title={`Site network · ${fmtDuration(netMin)}`} />}
          {devMin > 0 && <div className="av-rb-seg device" style={{ width: `${(devMin / scheduledMin * 100).toFixed(3)}%`, minWidth: 6 }} title={`Device off · ${fmtDuration(devMin)}`} />}
        </div>
        <div className="av-rb-legend">
          <span><span className="av-rb-sw" style={{ background: 'var(--fault-green)' }} />Up <strong>{fmtDuration(uptimeMin)}</strong></span>
          <span><span className="av-rb-sw our" />App issue <strong>{fmtDuration(ourMin)}</strong></span>
          <span><span className="av-rb-sw net" />Site network <strong>{fmtDuration(netMin)}</strong></span>
          <span><span className="av-rb-sw device" />Device off / reboot <strong>{fmtDuration(devMin)}</strong></span>
        </div>
      </div>

      <div className="av-rel-sites">
        <div className="av-rs-eyebrow">By site · {bySite.length} sites</div>
        {bySite.map((s) => {
          const sourMin = s.breakdown?.app?.minutes ?? 0;
          const snetMin = s.breakdown?.network?.minutes ?? 0;
          const sdevMin = s.breakdown?.device?.minutes ?? 0;
          const sTotalDark = sourMin + snetMin + sdevMin;
          // Per-site uptime % comes from backend now (computed against
          // that site's own device-count × observable days). Use it
          // directly instead of re-deriving on the client.
          const sUptime = s.uptimePct ?? 100;
          const sSched = s.scheduledMinutes || 1;
          return (
            <div key={s.siteSlug} className="av-rs-row">
              <div className="av-rs-name">{s.siteName} <span className="meta">· {sTotalDark === 0 ? '0 incidents' : fmtDuration(sTotalDark) + ' dark'}</span></div>
              <div className="av-rs-bar">
                {snetMin > 0 && <div className="seg" style={{ background: 'var(--fault-blue)',   width: `${Math.min(50, snetMin / sSched * 100)}%`, minWidth: 4 }} />}
                {sdevMin > 0 && <div className="seg" style={{ background: 'var(--fault-red)',    width: `${Math.min(50, sdevMin / sSched * 100)}%`, minWidth: 4 }} />}
                {sourMin > 0 && <div className="seg" style={{ background: 'var(--fault-orange)', width: `${Math.min(50, sourMin / sSched * 100)}%`, minWidth: 4 }} />}
              </div>
              <div className="av-rs-pct">{sUptime.toFixed(2)}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Root ───
export default function AnalyticsView() {
  const [period, setPeriod] = useState('month');
  const [reliability, setReliability] = useState(null);
  const [truthCheck, setTruthCheck]   = useState(null);
  const [library, setLibrary]         = useState(null);
  const [airtime, setAirtime]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    const { from, to } = computeRange(period);
    const params = { from, to, projectId: PROJECT_ID };
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAnalyticsLibrary({ ...params, limit: 10 }),
      getAnalyticsAirtime({ ...params, groupBy: 'site' }),
      getAnalyticsTruthCheck(params),
      getAnalyticsReliability(params),
    ]).then(([lib, at, tc, rel]) => {
      if (cancelled) return;
      setLibrary(lib);
      setAirtime(at);
      setTruthCheck(tc);
      setReliability(rel);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      console.error('[analytics] fetch failed', err);
      setError(err?.response?.data?.error || err.message || 'Unknown error');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [period]);

  return (
    <>
      <style>{STYLE}</style>
      <div className="av-root">
        <div className="av-page">
          <PeriodPicker value={period} onChange={setPeriod} />
          <LibrarySection data={library} loading={loading} error={error} period={period} />
          <AirtimeSection data={airtime} loading={loading} error={error} period={period} />
          <TruthCheckSection data={truthCheck} period={period} loading={loading} error={error} />
          <ReliabilitySection data={reliability} loading={loading} error={error} period={period} />
        </div>
      </div>
    </>
  );
}
